import { useQuery } from '@tanstack/react-query';
import TagManager from 'react-gtm-module';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDeals } from '../api';
import { Deal } from '../types';
import { DealCard, DealsViewToggle, type DealsView } from '../components/deals';
import { SearchInput, SimpleSpinner } from '../components';
import { useCurrentUser, useDebounce, useIsSchoolUser, useScrollToTop } from 'src/hooks';
import Lottie from 'lottie-react';
import cartAnimation from '../images/lotties/cash-cart.json';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const PAGE_SIZE = 20;
const CART_PAGE_SOURCES = ['get', 'receive', 'make', 'share', 'embed', 'widget'];

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

function matchesDeal(deal: Pick<Deal, 'retailer' | 'name' | 'description'>, query: string): boolean {
  const normQuery = normalize(query);
  if (!normQuery) return false;
  const fields = [deal.retailer, deal.name, deal.description ?? ''].map(normalize).join(' ');
  if (fields.includes(normQuery)) return true;
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => fields.includes(normalize(word)));
}

export const Deals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const vendorParam = searchParams.get('vendor');
  const fromCart = CART_PAGE_SOURCES.includes(searchParams.get('from') ?? '');
  const [sendSearchAnalytics, setSendSearchAnalytics] = useState(false);
  const [search, setSearch] = useState(() => vendorParam ?? '');
  const [debounceDelay, setDebounceDelay] = useState(500);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isSchoolUser = useIsSchoolUser();
  const { isLoading: userLoading } = useCurrentUser();

  // School users default to the schools view; an explicit ?view= param wins so
  // a shared link shows the same view to everyone.
  const viewParam = searchParams.get('view');
  const view: DealsView =
    viewParam === 'schools'
      ? 'schools'
      : viewParam === 'all'
        ? 'all'
        : isSchoolUser
          ? 'schools'
          : 'all';
  const showToggle = isSchoolUser || searchParams.has('view');

  useScrollToTop();

  // The landing view depends on the user's email (school default), so wait for
  // the currentUser query to settle before firing — otherwise school users
  // would be logged as landing on 'all'. Ref-guarded to fire exactly once.
  const firedPageView = useRef(false);
  useEffect(() => {
    if (userLoading || firedPageView.current) return;
    firedPageView.current = true;
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: 'Page View',
        eventValue: {
          source: searchParams.get('from') ?? 'direct',
          view,
          ...(searchParams.get('vendor') && { vendor: searchParams.get('vendor') }),
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLoading]);

  const debouncedSearch = useDebounce(search, debounceDelay);

  const dealsQuery = useQuery({
    queryKey: ['deals'],
    queryFn: getDeals,
    staleTime: 1000 * 60 * 10,
  });

  const allDeals = dealsQuery.data ?? [];
  const baseDeals = view === 'schools' ? allDeals.filter((d) => d.school) : allDeals;

  const filteredDeals = debouncedSearch.trim()
    ? baseDeals.filter((d) => matchesDeal(d, debouncedSearch))
    : baseDeals;

  const visibleDeals = filteredDeals.slice(0, visibleCount);
  const hasMore = visibleCount < filteredDeals.length;

  // Reset pagination when filter changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [debouncedSearch, view]);

  // Track search events. We only fire when the user has actually typed something — the search
  // input can be pre-populated from the ?vendor= URL param (e.g. when the user arrives from
  // the navbar deals button on a vendor cart page). That pre-population is not a user-initiated
  // search and is already captured in the Page View event via the `vendor` param.
  // sendSearchAnalytics is set to true in the onChange handler below so we can distinguish
  // URL-param pre-population from actual user input.
  useEffect(() => {
    if (!sendSearchAnalytics) return;
    if (!debouncedSearch.trim()) return;
    if (dealsQuery.status !== 'success') return;
    const count = filteredDeals.length;
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: count === 0 ? 'Search No Results' : 'Search',
        eventValue: {
          query: debouncedSearch.trim(),
          ...(count > 0 && { resultCount: count }),
        },
      },
    });
  }, [debouncedSearch, dealsQuery.status, filteredDeals.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((c) => c + PAGE_SIZE);
      },
      { rootMargin: '200px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredDeals]);

  // Fire immediately when input is cleared
  useEffect(() => {
    if (!search) {
      setDebounceDelay(0);
      setTimeout(() => setDebounceDelay(500), 20);
    }
  }, [search]);

  // Enter fires immediately, then restores the debounce delay
  const handleEnterPress = () => {
    setDebounceDelay(0);
    setTimeout(() => setDebounceDelay(500), 20);
  };

  // Always write the param explicitly so a non-school visitor arriving on a
  // shared ?view=schools link can toggle both directions (the toggle stays
  // visible as long as the param is present).
  const handleViewChange = (next: DealsView) => {
    if (next === view) return;
    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    setSearchParams(params, { replace: true });
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: 'View Toggle',
        eventValue: { view: next },
      },
    });
  };

  return (
    <>
      {/* Hero */}
      <div className='relative flex w-full flex-col items-center gap-6 bg-primary-dark px-6 pb-6 pt-0'>
        {fromCart && (
          <button
            onClick={() => navigate(-1)}
            className='heading-100 absolute left-6 top-6 inline-flex items-center whitespace-nowrap text-skyBlue transition-all hover:text-white'
          >
            <ChevronLeftIcon className='-ml-1.5 -mr-0.5 -mt-0.5' />
            Back to Cart
          </button>
        )}
        <div className='flex flex-col items-center gap-6 text-center'>
          <Lottie
            animationData={cartAnimation}
            loop={true}
            autoplay={true}
            style={{ width: '128px', height: 'auto' }}
          />
          <h1 className='heading-700 text-offWhite'>
            {view === 'schools' ? 'Deals for Schools' : 'Deals'}
          </h1>
          <div className='flex flex-col items-center gap-1'>
            <p className='text-300 text-blueGray'>
              {view === 'schools'
                ? 'Search for deals specifically for schools, educators, and students below:'
                : 'Save money and get special deals, only for Share-A-Cart users'}
            </p>
            <a
              href='/save'
              data-attr='deals-readmore-save'
              className='text-200 text-skyBlue underline transition-opacity duration-150 hover:opacity-80'
            >
              Read more about deals here
            </a>
          </div>
        </div>

        {showToggle && (
          <div className='w-full max-w-[720px]'>
            <DealsViewToggle view={view} onChange={handleViewChange} />
          </div>
        )}

        <div className='w-full max-w-md'>
          <SearchInput
            value={search}
            onChange={(val) => {
              setSendSearchAnalytics(true);
              setSearch(val);
            }}
            placeholder='Search by retailer or keyword'
            onEnterPress={handleEnterPress}
          />
        </div>
      </div>

      {/* Deals list */}
      <div className='mx-auto flex w-full max-w-3xl flex-col gap-4 px-4 py-8'>
        {debouncedSearch.trim() && (
          <span className='text-200 text-primary-dark'>
            Showing results for &ldquo;{debouncedSearch}&rdquo;
          </span>
        )}

        {dealsQuery.isLoading ? (
          <div className='mx-auto py-20'>
            <SimpleSpinner customWidth='w-16' customHeight='h-16' customPosition='block' />
          </div>
        ) : dealsQuery.isError ? (
          <div className='py-20 text-center'>
            <p className='text-200 text-offGray'>
              Something went wrong loading deals. Please try again.
            </p>
          </div>
        ) : filteredDeals.length === 0 ? (
          <div className='py-20 text-center'>
            <p className='text-200 text-offGray'>
              {debouncedSearch
                ? `No deals found for "${debouncedSearch}".`
                : view === 'schools'
                  ? 'No school deals available right now. Check back soon!'
                  : 'No deals available right now. Check back soon!'}
            </p>
          </div>
        ) : (
          <>
            <p className='text-100 text-offGray'>
              {filteredDeals.length} {filteredDeals.length === 1 ? 'offer' : 'offers'} available
            </p>

            <div className='flex flex-col gap-4'>
              {visibleDeals.map((deal, i) => (
                <DealCard key={`${deal.retailer}-${deal.name}-${i}`} deal={deal} />
              ))}
            </div>
            {hasMore && <div ref={sentinelRef} className='h-1' />}
          </>
        )}
      </div>
    </>
  );
};
